// progress-bar — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    width: 100%;
  }

  [part="track"] {
    position: relative;
    width: 100%;
    height: var(--run-progress-bar-height, 8px);
    background: var(--run-progress-bar-track-bg, var(--run-color-surface-subtle));
    border-radius: var(--run-progress-bar-radius, var(--run-radius-semantic-pill));
    overflow: hidden;
  }

  [part="fill"] {
    inline-size: var(--run-progress-fill-width, 0%);
    height: 100%;
    border-radius: var(--run-progress-bar-radius, var(--run-radius-semantic-pill));
    background: var(--run-progress-bar-fill-bg, var(--run-color-intent-primary-base));
    transform-origin: inline-start center;
    transition:
      inline-size var(--run-progress-bar-duration, var(--run-motion-semantic-standard-duration)) var(--run-progress-bar-easing, var(--run-motion-semantic-standard-easing)),
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-standard-easing);
  }

  :host([data-mode="indeterminate"]) [part="fill"] {
    inline-size: 40% !important;
    animation: run-pb-sweep var(--run-motion-semantic-attention-duration) linear infinite;
    transition: none;
  }

  :host([intent="primary"]) [part="fill"] {
    background: var(--run-progress-bar-fill-bg, var(--run-color-intent-primary-base));
  }
  :host([intent="success"]) [part="fill"] {
    background: var(--run-progress-bar-fill-bg, var(--run-color-intent-success-base));
  }
  :host([intent="warning"]) [part="fill"] {
    background: var(--run-progress-bar-fill-bg, var(--run-color-intent-warning-base));
  }
  :host([intent="danger"]) [part="fill"] {
    background: var(--run-progress-bar-fill-bg, var(--run-color-intent-danger-base));
  }

  :host([size="sm"]) [part="track"] { height: var(--run-progress-bar-height, 4px); }
  :host([size="md"]) [part="track"] { height: var(--run-progress-bar-height, 8px); }
  :host([size="lg"]) [part="track"] { height: var(--run-progress-bar-height, 12px); }

  :host([data-complete]) [part="fill"] {
    transition:
      inline-size var(--run-progress-bar-duration, var(--run-motion-semantic-standard-duration)) var(--run-progress-bar-easing, var(--run-motion-semantic-standard-easing)),
      background var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-entrance-easing);
  }

  [part="label-row"] {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: var(--run-space-semantic-xs);
    margin-block-end: var(--run-progress-bar-label-gap, var(--run-space-semantic-xs));
  }

  [part="label"] {
    color: var(--run-progress-bar-label-color, var(--run-color-text-primary));
    font-size: var(--run-progress-bar-label-font-size, var(--run-font-size-sm));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  [part="value"] {
    color: var(--run-progress-bar-label-color, var(--run-color-text-primary));
    font-size: var(--run-progress-bar-label-font-size, var(--run-font-size-sm));
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }

  @keyframes run-pb-sweep {
    from { transform: translateX(-100%); }
    to   { transform: translateX(300%); }
  }

  @media (prefers-reduced-motion: reduce) {
    [part="fill"] {
      transition: none;
    }
    :host([data-mode="indeterminate"]) [part="fill"] {
      animation-duration: calc(var(--run-motion-semantic-attention-duration) * 2);
      animation-name: run-pb-sweep-reduced;
    }
    [part="value"] {
      transition: opacity var(--run-motion-semantic-micro-duration) var(--run-motion-semantic-entrance-easing);
    }
  }

  @keyframes run-pb-sweep-reduced {
    from { transform: translateX(-20%); }
    to   { transform: translateX(180%); }
  }
`;

export function render(host) {
  const {
    label,
    min,
    max,
    showValue,
    dataset,
    _coercedValue,
  } = host; // declared in MVA SkinModule.render contract

  const isIndeterminate = dataset.mode === 'indeterminate';

  const range = max - min;
  const fraction = (isIndeterminate || range <= 0)
    ? 0
    : Math.min(1, Math.max(0, (_coercedValue - min) / range));

  // Value text stays in the DOM while indeterminate, hidden via inline
  // visibility so the label row does not reflow when progress resumes.
  const valueHTML = showValue
    ? `<span part="value"${isIndeterminate ? ' style="visibility: hidden;"' : ''}>${new Intl.NumberFormat(undefined, { style: 'percent' }).format(fraction)}</span>`
    : '';

  const labelSpan = label ? `<span part="label"></span>` : '';
  const labelRowHTML = (label || showValue)
    ? `<div part="label-row">${labelSpan}${valueHTML}</div>`
    : '';

  // Fill width is owned by the headless base via --run-progress-fill-width
  // (written in updated() after every render).
  host.shadowRoot.innerHTML = `${labelRowHTML}<div part="track"><div part="fill"></div></div>`;

  if (label) {
    host.shadowRoot.querySelector('[part="label"]').textContent = label;
  }
}